import {Deck} from "../types/decks";
import {createContext, useContext, useEffect, useState} from "react";
import {useApiRequest} from "../../../common/hooks/useApiRequest";
import {useAuth} from "../../auth/context/AuthContext";
import {useQuery} from "@tanstack/react-query";

export interface DeckContextType {
    allDecks: Deck[] | null
    currentDeck: Deck | null
    changeCurrentDeck: (deck: Deck) => void
    ready: boolean
    isError: boolean
    error: string | null
}

const DeckContext = createContext<DeckContextType | null>(null);

export const useDeck = () => useContext(DeckContext)!;

export function DeckProvider({ children }: { children: React.ReactNode }) {

    const {token} = useAuth();

    const {getRequest, postRequest} = useApiRequest();

    const [currentDeck, setCurrentDeck] = useState<Deck | null>(null);
    const [ready, setReady] = useState<boolean>(false);

    const {data: allDecks = null, isError, error} = useQuery({
        queryKey: ['decks'],
        queryFn: () => getRequest<Deck[]>('decks'),
        enabled: !!token,
    });

    useEffect(() => {
        setCurrentDeck(allDecks?.find(deck => deck.isCurrent) || null);
        setReady(!!allDecks);
    }, [allDecks]);

    const changeCurrentDeck = (deck: Deck) => {
        setCurrentDeck(deck);
        postRequest('decks/current', { deckId: deck.deckId }).catch(() => {});
    }

    const value: DeckContextType = {
        allDecks,
        currentDeck,
        changeCurrentDeck,
        ready,
        isError,
        error: error ? error.message : null,
    }

    return (
        <DeckContext.Provider value={value}>
            {children}
        </DeckContext.Provider>
    );

}